import { Box } from "@mui/material"
import { Chart, registerables } from "chart.js"
import type { TooltipItem } from "chart.js"
import { useEffect, useRef, useState } from "react"
import ChartDataLabels from "chartjs-plugin-datalabels"

Chart.register(...registerables)
Chart.register(ChartDataLabels as any)

type FeelingDoughnutProps = {
  chartColors: string[]
  chartData: number[]
  showPercentaces: boolean
  useFullSize?: boolean
  chartLabels?: string[]
}

const FeelingDoughnut = ({
  chartData,
  chartColors,
  showPercentaces,
  useFullSize,
  chartLabels,
}: FeelingDoughnutProps) => {
  const [chartWidth, setChartWidth] = useState("100%")

  const containerRef = useRef<HTMLDivElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const chartRef = useRef<Chart<"doughnut", number[], unknown> | null>(null)
  const latestLabelsRef = useRef<string[] | undefined>(chartLabels)
  const latestDataRef = useRef<number[]>(chartData)
  const latestShowRef = useRef<boolean>(showPercentaces)

  useEffect(() => {
    if (!containerRef.current) return

    let rafId: number | null = null
    const onResize = () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        chartRef.current?.resize()
      })
    }

    // Prefer observing the container element; this catches layout changes that don't
    // necessarily align with the window resize event (e.g. devtools device toggle).
    const ro = new ResizeObserver(onResize)
    ro.observe(containerRef.current)

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      ro.disconnect()
    }
  }, [])

  useEffect(() => {
    latestLabelsRef.current = chartLabels
  }, [chartLabels])

  useEffect(() => {
    latestDataRef.current = chartData
  }, [chartData])

  useEffect(() => {
    latestShowRef.current = showPercentaces
  }, [showPercentaces])

  const chartResize = () => {
    if (useFullSize) {
      setChartWidth("100%")
      requestAnimationFrame(() => {
        chartRef.current?.resize()
      })
      return
    }

    if (document.getElementById("ClientEntityList")) {
      setChartWidth("100%")
    } else {
      if (window.innerWidth < 570) {
        setChartWidth("100%")
      } else if (window.innerWidth >= 570 && window.innerWidth < 1100) {
        setChartWidth("50%")
      } else if (window.innerWidth >= 1100) {
        setChartWidth("35%")
      }
    }

    // Let layout settle, then tell Chart.js to recompute its size.
    requestAnimationFrame(() => {
      chartRef.current?.resize()
    })
  }

  useEffect(() => {
    if (useFullSize) {
      setChartWidth("100%")
      requestAnimationFrame(() => {
        chartRef.current?.resize()
      })
    } else {
      chartResize() //Called here to set correct chart width during first page load.
    }
    window.addEventListener("resize", chartResize)
    return () => {
      window.removeEventListener("resize", chartResize)
    }
  }, [useFullSize])

  useEffect(() => {
    if (!canvasRef.current || chartRef.current) return

    const ctx = canvasRef.current.getContext("2d")
    if (!ctx) return

    chartRef.current = new Chart<"doughnut", number[], unknown>(ctx, {
      type: "doughnut",
      data: {
        datasets: [
          {
            data: chartData,
            backgroundColor: chartColors,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          datalabels: {
            backgroundColor: "white",
            borderColor: "white",
            borderRadius: 25,
            borderWidth: 2,
            color: "black",
            font: {
              weight: "bold",
            },
            padding: 6,
            formatter: (value: number, context: any) => {
              const chart = context?.chart
              const datasetIndex = context?.datasetIndex ?? 0
              const rawData = chart?.data?.datasets?.[datasetIndex]?.data ?? []

              const summed = Array.isArray(rawData)
                ? rawData.reduce((acc: number, v: unknown) => acc + (typeof v === "number" ? v : 0), 0)
                : 0

              if (!summed) return "0%"
              return ((value * 100) / summed).toFixed(0) + "%"
            },
            display: () => latestShowRef.current,
          },
          tooltip: {
            callbacks: {
              label: function (tooltipItem: TooltipItem<"doughnut">) {
                const index = tooltipItem.dataIndex
                const labels = latestLabelsRef.current ?? latestDataRef.current.map((x) => x.toString())
                return labels[index]
              },
            },
          },
        },
      },
    })

    return () => {
      chartRef.current?.destroy()
      chartRef.current = null
    }
  }, [])

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.data.datasets = [
        {
          data: chartData,
          backgroundColor: chartColors,
        },
      ]

      chartRef.current.update()
    }
  }, [chartData, chartColors])

  return (
    <Box ref={containerRef} sx={{ mx: useFullSize ? 0 : "auto", my: useFullSize ? 0 : "auto", width: chartWidth }}>
      <canvas ref={canvasRef}></canvas>
    </Box>
  )
}

export default FeelingDoughnut
